import { useState } from "react";
import { toast } from "sonner";
import { Video, CheckCircle2, MessageSquarePlus, Copy, Lock } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { useCoachReviews, useCompleteReviewTask, useFollowUpOnReview, useTestimonialExport } from "@/lib/reviewQueries";

const QUESTION_LABELS = {
  challenge: "What challenge were they facing?",
  collaboration: "How did we work together?",
  outcome: "What outcome did they achieve?",
  outcome_link: "How did the work help them reach that outcome?",
  most_important: "What was the most important part of the journey?",
  what_would_change: "What would they change if they started again?",
};

/**
 * Master v5 Prompt 15 (minimal coach-only surface) extended in place by
 * Master v5 Prompt 19 into the fuller Reviews workbench: a queue of
 * every submitted review across every Workspace this coach coaches,
 * with ratings/video/testimonial-consent status visible, plus
 * "Acknowledge" and "Follow up" — neither of which EVER writes to
 * review_answers/review_submissions (that data stays exactly what the
 * client submitted). Testimonial export/use is only ever reachable, and
 * only ever returns content, when the CURRENT consent row is 'granted'
 * — a stale or withdrawn/declined row always 403s server-side, this
 * page never guesses around that.
 */
export default function CoachReviewsPage() {
  const { data, isPending, isError, error } = useCoachReviews();
  const completeTask = useCompleteReviewTask();

  return (
    <div data-testid="coach-reviews-page">
      <h1 className="font-serif text-2xl text-ink mb-1">Reviews</h1>
      <p className="text-sm text-ink-soft mb-6">Submitted client reviews across your Workspaces — ratings, video and testimonial consent.</p>

      {isPending ? (
        <p className="text-sm text-ink-soft" data-testid="coach-reviews-loading">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-red-600" data-testid="coach-reviews-error">
          {error?.status === 403 ? "Coach access required to view this page." : "Couldn't load reviews."}
        </p>
      ) : data.items.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid="coach-reviews-empty">No reviews submitted yet.</p>
      ) : (
        <div className="space-y-4 max-w-3xl">
          {data.items.map((item) => {
            const task = data.tasks.find((t) => t.source_id === item.submission.rsb_id);
            return (
              <ReviewCard key={item.submission.rsb_id} item={item} task={task} onAcknowledge={() => completeTask.mutate(task.ctk_id)} />
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReviewCard({ item, task, onAcknowledge }) {
  const rsbId = item.submission.rsb_id;
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUpText, setFollowUpText] = useState("");
  const followUp = useFollowUpOnReview();
  const { refetch: fetchExport, isFetching: exportLoading } = useTestimonialExport(rsbId);
  const consentGranted = item.consent?.consent_status === "granted";

  async function handleSendFollowUp() {
    if (!followUpText.trim()) return;
    try {
      await followUp.mutateAsync({ rsbId, bodyText: followUpText.trim() });
      toast.success("Sent — visible in Messages");
      setFollowUpText(""); setFollowUpOpen(false);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't send that follow-up");
    }
  }

  async function handleCopyTestimonial() {
    const { data: exp, error: expErr } = await fetchExport();
    if (expErr || !exp) {
      toast.error(expErr?.status === 403 ? "Consent doesn't currently permit a testimonial export" : "Couldn't export testimonial");
      return;
    }
    const attribution = [exp.attribution?.display_name, exp.attribution?.role_title, exp.attribution?.company_name].filter(Boolean).join(", ");
    const text = [exp.quote, attribution && `— ${attribution}`].filter(Boolean).join("\n");
    await navigator.clipboard.writeText(text || "(no written quote permitted by current consent)");
    toast.success("Testimonial copied — allowed channels: " + (exp.allowed_channels.join(", ") || "none selected"));
  }

  return (
    <div className="border border-ink/15 bg-white/50 rounded-lg p-5" data-testid={`coach-review-item-${rsbId}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="font-medium text-ink">{item.member_name || "Client"} — {item.workspace_title}</p>
          <p className="text-xs text-ink-soft">Submitted {new Date(item.submission.submitted_at).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setFollowUpOpen((v) => !v)} className="text-xs text-ink-soft hover:text-terracotta flex items-center gap-1" data-testid={`coach-review-followup-toggle-${rsbId}`}>
            <MessageSquarePlus size={12} /> Follow up
          </button>
          {task?.status === "open" && (
            <button onClick={onAcknowledge} className="text-xs text-terracotta hover:underline" data-testid={`coach-review-complete-task-${rsbId}`}>
              Acknowledge
            </button>
          )}
        </div>
      </div>
      <p className="text-sm text-ink-soft mb-2">
        Coach rating: {item.submission.coach_rating}/5 · Programme rating: {item.submission.programme_rating}/5
        {item.submission.video_fas_id && <span className="inline-flex items-center gap-1 ml-2"><Video size={12} /> Video attached</span>}
      </p>
      <div className="flex items-center gap-2 mb-3">
        <p className="text-xs text-ink-soft">Testimonial consent: {item.consent?.consent_status || "not yet decided"}</p>
        {consentGranted ? (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={handleCopyTestimonial} disabled={exportLoading} data-testid={`coach-review-export-testimonial-${rsbId}`}>
            <Copy size={11} className="mr-1" /> {exportLoading ? "Exporting…" : "Copy testimonial"}
          </Button>
        ) : (
          <span className="text-xs text-ink-soft/60 flex items-center gap-1" data-testid={`coach-review-export-locked-${rsbId}`}><Lock size={10} /> export locked</span>
        )}
      </div>
      {followUpOpen && (
        <div className="mb-3 p-3 bg-ink/[0.03] rounded space-y-2" data-testid={`coach-review-followup-panel-${rsbId}`}>
          <Textarea value={followUpText} onChange={(e) => setFollowUpText(e.target.value)} rows={2} placeholder="Message the client — sent via Messages, never edits their answers…" data-testid={`coach-review-followup-input-${rsbId}`} />
          <Button size="sm" onClick={handleSendFollowUp} disabled={!followUpText.trim() || followUp.isPending} data-testid={`coach-review-followup-send-${rsbId}`}>
            {followUp.isPending ? "Sending…" : "Send"}
          </Button>
        </div>
      )}
      <div className="space-y-2">
        {Object.entries(item.answers).map(([key, text]) => (
          <div key={key}>
            <p className="text-xs text-ink-soft">{QUESTION_LABELS[key] || key}</p>
            <p className="text-sm text-ink" data-testid={`coach-review-answer-${rsbId}-${key}`}>{text}</p>
          </div>
        ))}
      </div>
      {task?.status === "completed" && (
        <p className="text-xs text-ink-soft mt-2 flex items-center gap-1"><CheckCircle2 size={12} /> Acknowledged</p>
      )}
    </div>
  );
}
